import { useEffect, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { ChallengeScheme } from "client-v2";

import PriceBreakdownCell from "../../../components/cell/PriceBreakdownCell";
import {
	Text,
	Color,
	ProgressBar,
	FAQs,
	ColorV2,
	Label,
} from "@adoptaunabuelo/react-components";
import { Users2, Flower, PartyPopper, Calendar, PenLine } from "lucide-react";
import ReliefModal from "../../../components/modal/ReliefModal";
import { useSelector } from "react-redux";
import { State } from "../../../redux";
import DonationShield from "../../../assets/images/icon/DonationShield";
import ProtectedDonationModal from "../../../components/modal/ProtectedDonationModal";

const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const Section = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 32px 0px;
	border-top: 1px solid ${ColorV2.border.neutralSoft};
	${media.lessThan("medium")`
        padding: 24px 0px;
    `}
`;
const MobileSection = styled.div`
	display: none;
	flex: 1;
	flex-direction: column;
	margin-top: 48px;
	${media.lessThan("medium")`
        display: flex;
    `}
`;
const Cell = styled.div`
	display: flex;
	flex-direction: row;
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Column = styled.div`
	display: flex;
	flex-direction: column;
`;
const DateView = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	min-height: 50px;
	min-width: 48px;
	border-radius: 6px;
`;
const Line = styled.div`
	display: flex;
	width: 1px;
	height: 100%;
	min-height: 20px;
	border-left: 2px solid ${Color.line.soft};
`;
const Image = styled.img`
	height: 225px;
	width: 400px;
	border-radius: 6px;
	margin-bottom: 12px;
	object-fit: cover;
	${media.lessThan("medium")`
        width: 100%;
    `}
`;
const SupporterImage = styled.img`
	height: 44px;
	width: 44px;
	min-height: 44px;
	min-width: 44px;
	border-radius: 44px;
	margin-right: 12px;
	object-fit: cover;
`;
const HowWork = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`;
const Numbers = styled(Text)`
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${ColorV2.surface.neutralSoft};
	margin-right: 16px !important;
	height: 40px;
	width: 40px;
	border-radius: 30px;
`;
const SpanLink = styled.span`
	cursor: pointer;
	text-decoration: underline;
`;
const Icon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 44px;
	width: 44px;
	border-radius: 8px;
	background-color: ${ColorV2.surface.background};
`;
const Cell2 = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	padding: 12px 16px;
	border-radius: 16px;
	gap: 12px;
	cursor: pointer;
`;
const Cell2Icon = styled.div`
	width: 40px;
	height: 40px;
`;
const Info = (props: Props) => {
	dayjs.extend(relativeTime);
	dayjs.locale("es");
	const appLocation = useSelector((state: State) => state.location.location);
	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);

	const [pastDate, setPastDate] = useState(false);
	const [completed, setCompleted] = useState(false);
	const [showReliefModal, setShowReliefModal] = useState(false);
	const [showProtectedModal, setShowProtectedModal] = useState(false);

	useEffect(() => {
		//Check if past date
		const diff = dayjs(props.challenge.endDate.iso).diff(dayjs(), "days");
		if (diff <= 0) setPastDate(true);

		//Check if goal completed
		const restAmount = props.challenge.goal - props.challenge.amount;
		if (restAmount <= 0) setCompleted(true);
	}, [props.challenge]);

	const calculateAmount = (amount: number): string => {
		const tempAmount =
			amount / (appLocation ? appLocation.currency.conversion : 1);
		const tempCurrencyName = appLocation
			? appLocation.currency.name
			: "eur";
		const tempLanguage = appLocation ? appLocation.language : "es-ES";

		return new Intl.NumberFormat(tempLanguage, {
			style: "currency",
			currency: tempCurrencyName,
			maximumFractionDigits: 0,
		}).format(tempAmount);
	};

	const renderCell = (data: {
		icon: React.ComponentType<any>;
		iconColor?: string;
		iconBackground?: string;
		title: string;
		subtitle: string;
	}) => (
		<Row style={{ gap: 12 }}>
			<Icon
				style={{
					backgroundColor: data.iconBackground
						? data.iconBackground
						: ColorV2.surface.background,
				}}
			>
				<data.icon
					style={{
						minHeight: 24,
						minWidth: 24,
					}}
					height={24}
					width={24}
					color={
						data.iconColor
							? data.iconColor
							: ColorV2.text.neutralHard
					}
				/>
			</Icon>
			<Column style={{ flex: 1 }}>
				<Text type="p" weight="medium">
					{data.title}
				</Text>
				<Text
					type="p"
					style={{
						color: ColorV2.text.neutralMedium,
					}}
				>
					{data.subtitle}
				</Text>
			</Column>
		</Row>
	);

	const renderCell2 = (data: {
		title: string;
		subtitle: string;
		background?: string;
		Icon: React.ElementType<any>;
		onClick: () => void;
	}) => (
		<Cell2 style={{ background: data.background }} onClick={data.onClick}>
			<Text type="p2" style={{ flex: 1 }}>
				<span style={{ fontWeight: 600, textDecoration: "underline" }}>
					{data.title}
				</span>{" "}
				{data.subtitle}
			</Text>
			<Cell2Icon>
				<data.Icon />
			</Cell2Icon>
		</Cell2>
	);

	const renderTimelineCell = (item: any, index: number) => (
		<Cell key={"timeline-cell-" + index}>
			<Column style={{ marginRight: 16, alignItems: "center" }}>
				<DateView
					style={{
						backgroundColor: item.color
							? item.color
							: ColorV2.surface.background,
					}}
				>
					<Text
						type="c1"
						style={{
							color: item.textColor
								? item.textColor
								: Color.text.high,
						}}
					>
						{dayjs(item.createdAt).format("MMM")}
					</Text>
					<Text
						type="h6"
						style={{
							color: item.textColor
								? item.textColor
								: Color.text.high,
						}}
					>
						{dayjs(item.createdAt).format("DD")}
					</Text>
				</DateView>
				{!(index + 1 === props.challenge.timeline.length) && <Line />}
			</Column>
			<Column style={{ paddingBottom: 26 }}>
				{item.image && <Image src={item.image} />}
				<Text type="h6" weight="medium" style={{ marginTop: 4 }}>
					{item.title}
				</Text>
				{item.subtitle && (
					<Text type="p" style={{ marginTop: 4 }}>
						{item.subtitle}
					</Text>
				)}
			</Column>
		</Cell>
	);

	return (
		<Container>
			<ReliefModal
				isVisible={showReliefModal}
				price={50}
				charge={"one"}
				onClose={() => setShowReliefModal(false)}
			/>
			<ProtectedDonationModal
				type={"challenge"}
				isVisible={showProtectedModal}
				onClose={() => setShowProtectedModal(false)}
			/>
			<MobileSection style={{ marginTop: 24 }}>
				{props.challenge.to && props.challenge.type === "signature" && (
					<Column style={{ marginBottom: 24 }}>
						<Text
							type="p2"
							style={{ color: ColorV2.text.neutralMedium }}
						>
							Dirigido a:
						</Text>
						<Text type="p" weight="medium">
							{props.challenge.to.title}
						</Text>
					</Column>
				)}
				<ProgressBar
					style={{ height: 8, marginBottom: 12 }}
					progress={[
						{
							value:
								(props.challenge.amount /
									props.challenge.goal) *
								100,
							color: completed
								? Color.status.color.success
								: props.challenge.type === "rose"
								? ColorV2.surface.redMedium
								: Color.background.primary,
						},
					]}
					animationTime={0.3}
					animationDelay={0.5}
				/>
				<Row style={{ alignItems: "center", marginBottom: 24 }}>
					<Text
						type="h3"
						weight={"medium"}
						style={{
							color: completed
								? Color.status.color.success
								: props.challenge.type === "rose"
								? ColorV2.surface.redMedium
								: Color.text.primary,
						}}
					>
						{props.challenge.type === "signature"
							? props.challenge.amount.toLocaleString()
							: calculateAmount(props.challenge.amount)}
					</Text>
					<Text
						type="p"
						style={{
							marginLeft: 8,
							marginTop: 4,
							color: ColorV2.text.neutralMedium,
						}}
					>
						de{" "}
						{props.challenge.type === "signature"
							? props.challenge.goal.toLocaleString() + " firmas"
							: calculateAmount(props.challenge.goal)}
					</Text>
				</Row>
			</MobileSection>
			<MobileSection
				style={{
					margin: 0,
					gap: 12,
				}}
			>
				{props.challenge.type !== "birthday" &&
					completed &&
					renderCell({
						icon: PartyPopper,
						iconColor: ColorV2.text.green,
						iconBackground: ColorV2.surface.greenSoft,
						title: "¡Conseguido!",
						subtitle:
							props.challenge.type === "dream"
								? "Puedes seguir colaborando y ayudarnos a cumplir los sueños de otros abuelos."
								: props.challenge.type === "signature"
								? "Hemos llegado al objetivo, pero puedes seguir firmando y ayudando."
								: "",
					})}
				{props.challenge.type === "rose" ? (
					<Row>
						<Users2
							style={{
								marginRight: 12,
								minHeight: 24,
								minWidth: 24,
							}}
							height={24}
							width={24}
							color={ColorV2.text.neutralHard}
						/>
						<Text
							type="p"
							style={{
								color: ColorV2.text.neutralMedium,
								marginTop: 1,
							}}
						>
							<span
								style={{
									fontWeight: 600,
									color: ColorV2.text.neutralHard,
								}}
							>
								{props.challenge.supporters.toLocaleString() +
									" personas "}
							</span>
							han colaborado enviando rosas
						</Text>
					</Row>
				) : (
					props.challenge.type !== "signature" &&
					renderCell({
						icon: Users2,
						title:
							props.challenge.supporters.toLocaleString() +
							" personas han colaborado",
						subtitle:
							props.supporters.length > 0
								? "Última colaboración " +
								  (
										dayjs(
											props.supporters[0].createdAt
										) as any
								  ).fromNow()
								: "",
					})
				)}
				{props.challenge.type === "signature" &&
					renderCell({
						icon: PenLine,
						title: "Última firma",
						subtitle:
							props.supporters.length > 0
								? (
										dayjs(
											props.supporters[0].createdAt
										) as any
								  ).fromNow()
								: "",
					})}
				{props.challenge.type === "rose" ? (
					<Row style={{ marginTop: 8 }}>
						<Flower
							style={{
								marginRight: 12,
								minHeight: 24,
								minWidth: 24,
							}}
							height={24}
							width={24}
							color={ColorV2.text.neutralHard}
						/>
						<Text type="p" style={{ marginTop: 1 }}>
							Enviar cada rosa tiene un coste de 5€
						</Text>
					</Row>
				) : (
					!completed &&
					!pastDate &&
					renderCell({
						icon: Calendar,
						title:
							dayjs(props.challenge.endDate.iso).diff(
								dayjs(),
								"days"
							) + " días",
						subtitle: "para completar el sueño",
					})
				)}
			</MobileSection>
			{props.challenge.type === "dream" && (
				<MobileSection
					style={{
						backgroundColor: ColorV2.surface.background,
						padding: 16,
						borderRadius: 12,
						marginTop: 24,
					}}
				>
					<Text type="p2" style={{ color: ColorV2.text.neutralHard }}>
						Recupera hasta el{" "}
						<span
							style={{
								backgroundColor: ColorV2.surface.secondary,
								padding: "0px 6px",
								borderRadius: 3,
								fontWeight: 500,
							}}
						>
							80%
						</span>{" "}
						de tus donaciones en la Declaración de la Renta.{" "}
						<SpanLink onClick={() => setShowReliefModal(true)}>
							Saber más
						</SpanLink>
					</Text>
				</MobileSection>
			)}
			<Section
				id="description"
				style={isMobile ? { marginTop: 24 } : { border: "none" }}
			>
				<Text type="h4" weight="semibold" style={{ marginBottom: 12 }}>
					{props.challenge.type === "signature"
						? "Por qué necesitamos tu firma"
						: props.challenge.type === "rose"
						? "Necesitamos tu ayuda"
						: "Conoce su sueño"}
				</Text>
				<Text type="p" style={{ whiteSpace: "pre-wrap" }}>
					<div
						dangerouslySetInnerHTML={{
							__html: props.challenge.history,
						}}
					/>
				</Text>
			</Section>
			{props.challenge.type === "rose" && (
				<Section>
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						Cómo funciona
					</Text>
					<HowWork>
						<Row>
							<Numbers type="p" weight="semibold">
								1
							</Numbers>
							<Column style={{ flex: 1 }}>
								<Text type="h6" weight="medium">
									Elige cuántas rosas quieres enviar
								</Text>
								<Text
									type="p"
									style={{
										color: Color.text.high,
										marginTop: 4,
									}}
								>
									Puedes enviar todas las rosas que quieras.
									Recuerda que puedes enviarlas hasta el 23 de
									abril. Cuantas más rosas envíes más amor
									regalarás este Sant Jordi.
								</Text>
							</Column>
						</Row>
						<Row>
							<Numbers type="p" weight="semibold">
								2
							</Numbers>
							<Column style={{ flex: 1 }}>
								<Text type="h6" weight="medium">
									Haz tu donación: ¡es deducible!
								</Text>
								<Text
									type="p"
									style={{
										color: Color.text.high,
										marginTop: 4,
									}}
								>
									Enviar cada rosa tiene un coste de 5€. Al
									ser una donación te podrás deducir hasta el
									80% en la declaración de la renta. Es decir,
									enviar una rosa realmente te costará solo
									1€.
								</Text>
							</Column>
						</Row>
						<Row>
							<Numbers type="p" weight="semibold">
								3
							</Numbers>
							<Column style={{ flex: 1 }}>
								<Text type="h6" weight="medium">
									Nuestro equipo se encargará de la entrega
								</Text>
								<Text
									type="p"
									style={{
										color: Color.text.high,
										marginTop: 4,
									}}
								>
									Nuestro equipo social se encargará de
									entregar todas las rosas. Gracias a la ayuda
									de la floristería barcelonesa Rosistirem
									podremos enviar flores a todas las
									residencias de mayores.
								</Text>
							</Column>
						</Row>
					</HowWork>
				</Section>
			)}
			{props.challenge.team && (
				<Section id="team">
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						El equipo que lo va a hacer posible
					</Text>
					<Column style={{ gap: 24 }}>
						{props.challenge.team.map((item, index) => (
							<Row key={"social-worker-" + index}>
								<SupporterImage src={item.image} />
								<Column>
									<Text type="p" weight="medium">
										{item.name}
									</Text>
									<Label
										style={{
											height: "unset",
											padding: "0px 4px",
											marginBottom: 6,
											marginTop: 2,
										}}
										text={item.department}
										size="small"
										color={ColorV2.text.primary}
									/>
									<Text type="p2">{item.description}</Text>
								</Column>
							</Row>
						))}
					</Column>
				</Section>
			)}
			{props.challenge.timeline && (
				<Section id="timeline">
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						Últimas actualizaciones
					</Text>
					{props.challenge.timeline.map((item, index) =>
						renderTimelineCell(item, index)
					)}
				</Section>
			)}
			{props.challenge.distribution && (
				<Section id="distribution">
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 8 }}
					>
						{props.challenge.type === "rose"
							? "¿A qué se destinan los 5€ que cuesta cada rosa?"
							: "¿A qué se destina tu colaboración?"}
					</Text>
					<Text type="p" style={{ marginBottom: 32 }}>
						{props.challenge.type === "rose"
							? "Con tu colaboración podemos hacer frente a los costes que supone enviar una rosa a cada persona mayor. Queremos ser transparentes y mostrarte el detalle de costes:"
							: "Con tu colaboración podemos hacer frente a los costes que supone cumplir el sueño de la persona mayor. Queremos ser transparentes y mostrarte el detalle de costes:"}
					</Text>
					<PriceBreakdownCell
						options={props.challenge.distribution}
					/>
				</Section>
			)}
			{props.challenge.type === "dream" && (
				<Section>
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 8 }}
					>
						Las ventajas de colaborar
					</Text>
					<Text type="p" style={{ marginBottom: 32 }}>
						Queremos que colaborar con Adopta Un Abuelo sea fácil
						para ti, por eso te garantizamos:
					</Text>
					<Row
						style={{
							gap: 16,
							flexDirection: isMobile ? "column" : "row",
						}}
					>
						{renderCell2({
							title: "Donación protegida.",
							subtitle:
								"Tu pago es seguro con cifrado SSL y se destina íntegramente a realizar el sueño.",
							background: ColorV2.border.primarySoft,
							Icon: () => <DonationShield size="large" />,
							onClick: () => setShowProtectedModal(true),
						})}
						{renderCell2({
							title: "Beneficios fiscales.",
							subtitle:
								"Puedes deducirte tu donación en la próxima Declaración de la Renta.",
							background: ColorV2.surface.secondarySoft,
							Icon: () => (
								<img
									style={{ width: 40, height: 40 }}
									src={require("../../../assets/images/icon/coins.webp")}
								/>
							),
							onClick: () => setShowReliefModal(true),
						})}
					</Row>
				</Section>
			)}
			<Section id="colaborators">
				<Text type="h4" weight="semibold" style={{ marginBottom: 24 }}>
					{props.challenge.type === "signature"
						? "Últimas personas que han firmado"
						: "Últimos colaboradores"}
				</Text>
				{props.supporters.map((item, index) => (
					<Cell
						key={"supporter-" + index}
						style={{ alignItems: "center", marginBottom: 12 }}
					>
						<SupporterImage
							src={
								item.user.image
									? item.user.image.url
									: require("../../../assets/images/icon/user_icon.png")
							}
						/>
						<Column>
							<Text type="p" weight="medium">
								{item.user.name}
							</Text>
							<Text
								type="p2"
								style={{ color: ColorV2.text.neutralMedium }}
							>
								Colaboró{" "}
								{(dayjs(item.createdAt) as any).fromNow()}
							</Text>
						</Column>
					</Cell>
				))}
			</Section>
			<Section id="faqs">
				<Text type="h4" weight="semibold" style={{ marginBottom: 24 }}>
					Preguntas frecuentes
				</Text>
				<FAQs
					options={
						props.challenge.type === "signature"
							? [
									{
										id: "item-1",
										title: "¿Qué se hace con las firmas conseguidas?",
										description:
											"Adopta Un Abuelo se encarga de presentar las firmas recogidas al organismo correspondiente para conseguir el objetivo.",
									},
									{
										id: "item-2",
										title: "¿Si conozco una petición, puedo proponerla?",
										description:
											"Por supuesto que sí. Envía un email a info@adoptaunabuelo.org contándonos la petición y nuestro equipo se pondrá en contacto contigo.",
									},
									{
										id: "item-3",
										title: "¿Hay otra forma de ayudar?",
										description:
											"Puedes ayudarnos compartiendo y dando difusión a esta petición para conseguir más apoyo.",
									},
							  ]
							: props.challenge.type === "rose"
							? [
									{
										id: "item-1",
										title: "¿Qué se hace con el dinero recaudado?",
										description:
											"El importe se destina a comprar la rosa y el envío. Adicionalmente se dona un euro que destinaremos para acompañar a personas mayores en situación de soledad durante todo el año.",
									},
									{
										id: "item-2",
										title: "¿Hay otra forma de ayudar que no sea con dinero?",
										description:
											"Puedes ayudarnos compartiendo y dando difusión a la iniciativa o puedes participar como voluntario descargando nuestra app.",
									},
									{
										id: "item-3",
										title: "¿Puedo saber qué abuelo ha recibido mi rosa?",
										description:
											"Las rosas las entregamos directamente en las más de 400 residencias con las que colaboramos y ellos se encargan de entregarlas a los mayores. Grabaremos toda la experiencia, pero por el momento no podemos decirte qué abuelo en concreto recibirá tu rosa.",
									},
							  ]
							: [
									{
										id: "item-1",
										title: "¿Qué se hace con el dinero recaudado?",
										description:
											"Se destina de forma íntegra a la realización del sueño.",
									},
									{
										id: "item-2",
										title: "¿Hay otra forma de ayudar que no sea con dinero?",
										description:
											"Puedes ayudarnos compartiendo y dando difusión a este sueño o puedes participar como voluntario descargando nuestra app.",
									},
									{
										id: "item-3",
										title: "¿Si conozco el sueño de una persona mayor, puedo proponerlo?",
										description:
											"Por supuesto que sí, estaremos encantados de conocer el sueño. Envía un email a info@adoptaunabuelo.org contándonos el sueño y nuestro equipo se pondrá en contacto contigo.",
									},
									{
										id: "item-4",
										title: "¿Qué ocurre si se recauda más dinero del objetivo?",
										description:
											"El importe extra recaudado será destinado íntegramente a los programas de acompañamiento a personas mayores en situación de soledad no deseada que desarrollamos desde Adopta Un Abuelo. Por cada 1,5€ recaudado se realiza una hora de acompañamiento.",
									},
							  ]
					}
				/>
			</Section>
		</Container>
	);
};
export default Info;
export interface Props {
	challenge: ChallengeScheme;
	supporters: {
		user: any;
		amount: number;
		createdAt: Date;
		letter: boolean;
	}[];
}
